export const notNullOrEmpty = (data: string): boolean =>
  data && data.trim().length > 0;

// using regex from https://stackoverflow.com/questions/7905929/how-to-test-valid-uuid-guid
export function isGuid(value: string): boolean {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(value);
}

export function uniqueFilter(value, index, self) {
  return self.indexOf(value) === index;
}

/**
 * The server's explanation for a 403 (e.g. the item's business unit is not assigned to the user); any other failure
 * gets `fallbackMessage`, because the body of other errors is not meant for users.
 */
export function getForbiddenMessage(error: unknown, fallbackMessage: string): string {
  const isForbidden: boolean = typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 403;
  return isForbidden ? getApiErrorMessage(error, fallbackMessage) : fallbackMessage;
}

/** Returns the most useful API error text for user-facing messages. */
export function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (typeof error === 'string') {
    return parseMessage(error) || fallbackMessage;
  }

  if (!error || typeof error !== 'object') {
    return fallbackMessage;
  }

  const source: Record<string, unknown> = error as Record<string, unknown>;

  // An ApiException's `message` is only the generic status text ("Forbidden"); the backend's own text is in `response`.
  if (typeof source.status === 'number' && typeof source.response === 'string') {
    const apiExceptionResponse: string = parseMessage(source.response);
    if (apiExceptionResponse) {
      return apiExceptionResponse;
    }
  }

  const directObjectMessage: string = pickMessageFromObject(source);
  if (directObjectMessage) {
    return directObjectMessage;
  }

  const responseMessage: string = typeof source.response === 'string' ? parseMessage(source.response) : '';
  if (responseMessage) {
    return responseMessage;
  }

  const errorMessage: string = typeof source.error === 'string' ? parseMessage(source.error) : '';
  if (errorMessage) {
    return errorMessage;
  }

  const message: string = typeof source.message === 'string' ? parseMessage(source.message) : '';
  return message || fallbackMessage;
}

function parseMessage(rawMessage: string): string {
  const trimmedMessage: string = rawMessage.trim();
  if (!trimmedMessage) {
    return '';
  }

  try {
    const parsedMessage: unknown = JSON.parse(trimmedMessage);
    if (typeof parsedMessage === 'string') {
      return parsedMessage.trim();
    }
    if (parsedMessage && typeof parsedMessage === 'object') {
      return pickMessageFromObject(parsedMessage as Record<string, unknown>);
    }
  } catch {
    // Keep raw non-JSON backend message.
  }

  return trimmedMessage;
}

function pickMessageFromObject(errorObject: Record<string, unknown>): string {
  const messageKeys: string[] = ['detail', 'title', 'message', 'error', 'error_description'];
  for (const key of messageKeys) {
    const value: unknown = errorObject[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  const errors: unknown = errorObject.errors;
  if (!errors || typeof errors !== 'object') {
    return '';
  }

  for (const fieldErrors of Object.values(errors as Record<string, unknown>)) {
    if (Array.isArray(fieldErrors)) {
      for (const fieldError of fieldErrors) {
        if (typeof fieldError === 'string' && fieldError.trim().length > 0) {
          return fieldError.trim();
        }
      }
    }
  }

  return '';
}
